import React from 'react';
import { Text, Flex, Box, TextField, Select, Checkbox } from '@scale/scaleui-radix';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import useCreateProjectInviteCard from './useCreateProjectInviteCard';
import {
  InsertOrderSelector,
  ExpirationDurationSelector,
} from 'react-scale/corp/views/CorpProjectGroupManager/views/ThroughputTabs/ForecastComponents/OnboardingComponents/shared';

const CreateProjectInviteCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const {
    projectId,
    expirationDays,
    batchName,
    insertOrder,
    columnIdWithWorkerIds,
    useLatestBatchExpiresAt,
    dryRun,
    setProjectId,
    setExpirationDays,
    setBatchName,
    setInsertOrder,
    setColumnIdWithWorkerIds,
    setUseLatestBatchExpiresAt,
    setDryRun,
    availableColumns,
    numWorkers,
  } = useCreateProjectInviteCard(index);

  return (
    <Flex direction="column" gap="4">
      <Text size="2" weight="medium">
        Send early access invitations to contributors for a project. Invites will be sent
        immediately to all eligible contributors.
      </Text>

      {/* Project ID */}
      <Box>
        <Flex direction="column" gap="1">
          <Flex direction="row" gap="1">
            <Text size="2" weight="medium">
              Project ID
            </Text>
            <Text size="2" color="red">
              *
            </Text>
          </Flex>
          <TextField.Root
            size="2"
            placeholder="Enter project ID"
            value={projectId}
            onChange={e => setProjectId(e.target.value)}
          />
        </Flex>
      </Box>

      {/* Column with Worker IDs */}
      <Box>
        <Flex direction="column" gap="1">
          <Flex direction="row" gap="1">
            <Text size="2" weight="medium">
              Column with Worker IDs
            </Text>
            <Text size="2" color="red">
              *
            </Text>
          </Flex>
          <Select.Root value={columnIdWithWorkerIds} onValueChange={setColumnIdWithWorkerIds}>
            <Select.Trigger className="w-full" placeholder="Select a column with worker IDs" />
            <Select.Content>
              {availableColumns.map(column => (
                <Select.Item key={column.id ?? column.header} value={column.id ?? ''}>
                  {column.header ?? column.id}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          {columnIdWithWorkerIds && (
            <Text size="1" color="neutral">
              {numWorkers} worker{numWorkers === 1 ? '' : 's'} found
            </Text>
          )}
        </Flex>
      </Box>

      {/* Batch Name (optional) */}
      <Box>
        <Flex direction="column" gap="1">
          <Flex direction="row" gap="1">
            <Text size="2" weight="medium">
              Batch Name
            </Text>
            <Text size="2" color="gray" weight="light">
              (optional)
            </Text>
          </Flex>
          <TextField.Root
            size="2"
            placeholder="Enter batch name"
            value={batchName ?? ''}
            onChange={e => setBatchName(e.target.value || undefined)}
          />
        </Flex>
      </Box>

      {/* Expiration Duration - using shared component */}
      <ExpirationDurationSelector
        value={expirationDays.toString()}
        onChange={value => setExpirationDays(parseInt(value))}
      />

      {/* Insert Order / Assignment Rank - using shared component */}
      <InsertOrderSelector value={insertOrder} onChange={setInsertOrder} />

      {/* Extend Earlier Invites and Dry Run Mode */}
      <Box>
        <Flex align="start" gap="2">
          <Checkbox
            checked={useLatestBatchExpiresAt}
            onCheckedChange={setUseLatestBatchExpiresAt}
          />
          <Text size="2">
            Let contributors from earlier invite groups access the project until the latest invite
            for this project expires. This will appear as a standard Marketplace project once their
            invite expires. If there are no earlier invite groups, this will be ignored.
          </Text>
        </Flex>
        <Flex align="start" gap="2">
          <Checkbox checked={dryRun} onCheckedChange={setDryRun} />
          <Flex direction="row" gap="1" align="end">
            <Text size="2">
              Dry Run Mode. Check eligibility without sending invites. Use this to preview which
              contributors would be invited.
            </Text>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
};

export default CreateProjectInviteCardComponent;
